import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import AuthenticationGuard from "components/ui/AuthenticationGuard";
import { useAuth } from "contexts/AuthContext";
import NotFound from "pages/NotFound";
import GalleryManagement from './pages/gallery-management';
import AdminLogin from './pages/admin-login';
import GalleryViewer from './pages/gallery-viewer';
import GalleryCreation from './pages/gallery-creation';
import ContributorDashboard from './pages/contributor-dashboard';
import OwnerDashboard from './pages/owner-dashboard';
import WelcomePage from './pages/welcome-page';
import UserDashboard from './pages/user-dashboard';
import UserRegistration from './pages/user-registration';
import GalleryAccess from './pages/gallery-access';
import ContributorSetup from './pages/contributor-setup';

const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, userProfile, loading, profileLoading } = useAuth();
  const userRole = userProfile?.role;

  // Show loading state while authentication or profile is loading
  if (loading || (isAuthenticated && profileLoading && !userProfile)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If no specific roles required, just check authentication
  if (requiredRoles?.length === 0) {
    return (
      <AuthenticationGuard isAuthenticated={isAuthenticated}>
        {children}
      </AuthenticationGuard>
    );
  }

  // Check if user has one of the required roles
  const hasRequiredRole = requiredRoles?.includes(userRole);

  return (
    <AuthenticationGuard 
      isAuthenticated={isAuthenticated && hasRequiredRole}
      currentUserRole={userRole}
      requiredRoles={requiredRoles}
    >
      {children}
    </AuthenticationGuard>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/" element={<WelcomePage />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/user-registration" element={<UserRegistration />} />
        <Route path="/gallery-access" element={<GalleryAccess />} />
        <Route path="/gallery-viewer" element={<GalleryViewer />} />
        <Route path="/contributor-setup" element={<ContributorSetup />} />
        
        {/* Protected Admin Routes */}
        <Route path="/gallery-management" element={
          <ProtectedRoute requiredRoles={['owner', 'contributor']}>
            <GalleryManagement />
          </ProtectedRoute>
        } />
        <Route path="/gallery-creation" element={
          <ProtectedRoute requiredRoles={['owner', 'contributor']}>
            <GalleryCreation />
          </ProtectedRoute>
        } />
        <Route path="/contributor-dashboard" element={
          <ProtectedRoute requiredRoles={['contributor']}>
            <ContributorDashboard />
          </ProtectedRoute>
        } />
        <Route path="/owner-dashboard" element={
          <ProtectedRoute requiredRoles={['owner']}>
            <OwnerDashboard />
          </ProtectedRoute>
        } />
        
        {/* Protected User Routes */}
        <Route path="/user-dashboard" element={
          <ProtectedRoute requiredRoles={['user']}>
            <UserDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
